import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {COMMENTS_API} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {COMMENT_TAG} from 'app/utils/rtk-tags';

import {
  AttachedCommentImage,
  CommentableContentType,
  CommentConversationSerializedType,
  CommentConversationStatus,
  CommentSerializedType,
  ListResponseType,
} from 'sweet-types';

import type {FilterContentType} from './types';

interface BaseCommentFetchType {
  version_url?: string;
  exclude_deleted_comments?: string;
  page?: string | number;
  page_size?: number;
}

interface FilteredCommentFetchType extends BaseCommentFetchType {
  object_pk: number;
  content_type: FilterContentType;
}

export type CommentFetchType = FilteredCommentFetchType | BaseCommentFetchType;

export type AddCommentProps = {
  object_pk: number;
  parentId: number;
  content_type: CommentableContentType;
  comment: string;
  comment_conversation?: Omit<
    CommentConversationSerializedType,
    'id' | 'status' | 'url'
  >;
  attachments: {images: Array<AttachedCommentImage>};
};

export type UpdateConversationProps = {
  status: CommentConversationStatus;
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [COMMENT_TAG]})
  .injectEndpoints({
    endpoints: (build) => ({
      comments: build.query<
        ListResponseType<CommentSerializedType>,
        CommentFetchType
      >({
        query: (params) => ({
          url: COMMENTS_API,
          params: params,
        }),
        providesTags: providesTagsByURL(COMMENT_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: (response) =>
                response?.message ?? gt('Unable to load comments.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
      }),
      addComment: build.mutation<unknown, AddCommentProps>({
        query: (body) => ({
          url: COMMENTS_API,
          method: 'POST',
          body,
        }),
        invalidatesTags: () => [{type: COMMENT_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to add comment.'),
            onSuccess: () => gt('Comment added successfully.'),
          })(args, api);
        },
      }),
      updateConversation: build.mutation<
        CommentConversationSerializedType,
        {
          url: string;
        } & UpdateConversationProps
      >({
        query: ({url, ...body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: () => [{type: COMMENT_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to update comment.'),
            onSuccess: () => gt('Comment updated successfully.'),
          })(args, api);
        },
      }),
      deleteComment: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: COMMENT_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to delete comment.'),
            onSuccess: () => gt('Comment was deleted successfully.'),
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useCommentsQuery: useComments,
  useAddCommentMutation: useAddComment,
  useDeleteCommentMutation: useDeleteComment,
  useUpdateConversationMutation: useUpdateConversation,
} = extendedAPI;
